import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withoutForbiddenElevation } from '@application/data-access';
import {
  ChapterResponse,
  TranscriptCueResponse,
} from '../entities/academy.model';
import { TranscriptMatch } from '../entities/academy-responses.model';

/**
 * HTTP client for chapters, captions (WebVTT) and transcript search over both live
 * recordings (`/LiveEvents/*`) and course items (`/Courses/{courseId}/items/{itemId}/*`).
 * Source of truth: `docs/academy-api-integration.md`. Chapter/transcript reads return
 * plain arrays; caption reads return raw WebVTT text (`responseType: 'text'`).
 *
 * Every read here is gated by the event's (or course item's) audience, so a 403 is an
 * expected answer rather than something a supervisor login could unlock: all of them
 * carry `withoutForbiddenElevation()` and never open the temporary-elevation modal.
 */
@Injectable({ providedIn: 'root' })
export class ContentReadService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /** `GET /LiveEvents/{id}/chapters` — chapter markers for a live recording. Plain array. */
  public chapters(eventId: string): Observable<ChapterResponse[]> {
    return this.http.get<ChapterResponse[]>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(eventId)}/chapters`,
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }

  /**
   * `GET /LiveEvents/{id}/transcript` — all transcript cues (every language) for a live
   * recording, each carrying its `TranscriptCueId`. Plain array. Mirror of `chapters`; the
   * producer authoring editor reads here (the generic OData `TranscriptCues` set has no
   * `LiveEventId` column, so it cannot be filtered by recording).
   */
  public transcriptCues(eventId: string): Observable<TranscriptCueResponse[]> {
    return this.http.get<TranscriptCueResponse[]>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(eventId)}/transcript`,
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }

  /** `GET /LiveEvents/{id}/captions` — available caption language codes. Plain array. */
  public captionLanguages(eventId: string): Observable<string[]> {
    return this.http.get<string[]>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(eventId)}/captions`,
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }

  /** `GET /LiveEvents/{id}/captions/{lang}` — raw WebVTT caption text for the language. */
  public caption(eventId: string, lang: string): Observable<string> {
    return this.http.get(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(
        eventId
      )}/captions/${encodeURIComponent(lang)}`,
      {
        context: withoutForbiddenElevation(),
        responseType: 'text',
        withCredentials: true,
      }
    );
  }

  /**
   * `GET /LiveEvents/{id}/transcript/search` — transcript matches for query `q`.
   * `q` is required; `lang` filters to a single language when present. Plain array.
   */
  public transcriptSearch(
    eventId: string,
    q: string,
    lang?: string
  ): Observable<TranscriptMatch[]> {
    return this.http.get<TranscriptMatch[]>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(
        eventId
      )}/transcript/search`,
      {
        context: withoutForbiddenElevation(),
        params: this.buildSearchParams(q, lang),
        withCredentials: true,
      }
    );
  }

  /** `GET /Courses/{courseId}/items/{itemId}/chapters` — chapter markers for a course item. Plain array. */
  public itemChapters(
    courseId: string,
    itemId: string
  ): Observable<ChapterResponse[]> {
    return this.http.get<ChapterResponse[]>(
      `${this.baseUrl}/Courses/${encodeURIComponent(
        courseId
      )}/items/${encodeURIComponent(itemId)}/chapters`,
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }

  /** `GET /Courses/{courseId}/items/{itemId}/captions/{lang}` — raw WebVTT caption text. */
  public itemCaption(
    courseId: string,
    itemId: string,
    lang: string
  ): Observable<string> {
    return this.http.get(
      `${this.baseUrl}/Courses/${encodeURIComponent(
        courseId
      )}/items/${encodeURIComponent(itemId)}/captions/${encodeURIComponent(
        lang
      )}`,
      {
        context: withoutForbiddenElevation(),
        responseType: 'text',
        withCredentials: true,
      }
    );
  }

  /**
   * `GET /Courses/{courseId}/items/{itemId}/transcript/search` — transcript matches.
   * `q` is required; `lang` filters to a single language when present. Plain array.
   */
  public itemTranscriptSearch(
    courseId: string,
    itemId: string,
    q: string,
    lang?: string
  ): Observable<TranscriptMatch[]> {
    return this.http.get<TranscriptMatch[]>(
      `${this.baseUrl}/Courses/${encodeURIComponent(
        courseId
      )}/items/${encodeURIComponent(itemId)}/transcript/search`,
      {
        context: withoutForbiddenElevation(),
        params: this.buildSearchParams(q, lang),
        withCredentials: true,
      }
    );
  }

  /** Build transcript-search params: required `q`, optional `lang`. */
  private buildSearchParams(q: string, lang?: string): HttpParams {
    let params = new HttpParams().set('q', q);
    if (lang) params = params.set('lang', lang);
    return params;
  }
}
